import { Square } from 'lucide-react';
import React, { useCallback, useEffect, useRef } from 'react';
import submitChatBtnIcon from '../assets/send_chat_btn.png';
import type { CatalogDocument } from '../hooks/useDocumentMention';
import DocumentChips from './DocumentChips';
import './SimpleMessageInput.css';

interface SimpleMessageInputProps {
  input: string;
  isLoading: boolean;
  isStopping?: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onStopStreaming?: () => void;
  placeholder?: string;
  selectedDocs?: CatalogDocument[];
  onDocRemove?: (docId: string) => void;
  mentionDropdown?: React.ReactNode;
}

const SimpleMessageInput: React.FC<SimpleMessageInputProps> = ({
  input,
  isLoading,
  isStopping = false,
  onInputChange,
  onSubmit,
  onKeyPress,
  onStopStreaming,
  placeholder = "Ask anything",
  selectedDocs,
  onDocRemove,
  mentionDropdown,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The box grows with its VALUE, not with keystrokes: a question that
  // arrives programmatically (the dashboard's "Ask about this", a ?prefill=
  // link) used to land in a one-row box with the rest hidden below the fold,
  // because the only resize ran inside the change handler.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (input) {
      el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
    }
  }, [input]);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputChange(e);
  }, [onInputChange]);

  return (
    <form onSubmit={onSubmit} className="simple-message-input-form">
      <div className="simple-message-input-container relative">
        {mentionDropdown}
        {selectedDocs && onDocRemove && (
          <DocumentChips docs={selectedDocs} onRemove={onDocRemove} />
        )}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInput}
          onKeyDown={onKeyPress}
          placeholder={placeholder}
          className="simple-message-input-field"
          disabled={isLoading}
          rows={1}
        />
          {isLoading && onStopStreaming ? (
            <button
              type="button"
              onClick={onStopStreaming}
              disabled={isStopping}
              className="simple-message-input-button simple-message-input-stop-button"
              title={isStopping ? "Stopping..." : "Stop generating"}
            >
              <Square className="w-4 h-4" fill="currentColor" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className="simple-message-input-button"
              title={isLoading ? "Sending..." : "Send message"}
            >
              <img src={submitChatBtnIcon} alt="Send message" className="simple-message-input-plus-icon" />
            </button>
          )}
      </div>
    </form>
  );
};

export default SimpleMessageInput;
